import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Stack,
  IconButton,
  Alert
} from '@mui/material';
import { Close as CloseIcon, Edit as EditIcon } from '@mui/icons-material';
import type { InsuranceType } from '../../../../../services/EncounterService/types/encounterTypes';
import { useInsuranceForm } from './hooks';
import {
  BasicInfoSection,
  PolicyInfoSection,
  InsuredInfoSection,
  DialogActionButtons
} from './components';

interface InsuranceEditDialogProps {
  insurance: InsuranceType | null;
  open: boolean;
  onClose: () => void;
  onInsuranceUpdated?: () => void;
}

const InsuranceEditDialog: React.FC<InsuranceEditDialogProps> = ({
  insurance,
  open,
  onClose,
  onInsuranceUpdated
}) => {
  const {
    formData,
    selectedPayer,
    sameAsPatient,
    error,
    payers,
    isEncounterCancelled,
    handleInputChange,
    handlePayerChange,
    handleSameAsPatientChange,
    handleSubmit,
    handleDelete,
    handleClose
  } = useInsuranceForm(insurance, open, onClose, onInsuranceUpdated);

  if (!insurance) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: isEncounterCancelled ? '#f5f5f5' : '#f8fafc',
          borderBottom: '1px solid #e2e8f0',
          p: 2.5
        }}
      >
        <Typography
          variant="h6"
          fontWeight={600}
          color={isEncounterCancelled ? '#9e9e9e' : '#1e293b'}
        >
          <EditIcon sx={{ mr: 1, fontSize: 'inherit' }} />
          {isEncounterCancelled ? 'View Insurance (Read-only)' : 'Edit Insurance'}
        </Typography>
        <IconButton onClick={handleClose} sx={{ color: '#64748b' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Stack spacing={3} sx={{ p: 3 }}>
          <BasicInfoSection
            priority={formData.priority || 1}
            selectedPayer={selectedPayer}
            payerCode={formData.payerCode || ''}
            payerName={formData.payerName || ''}
            payers={payers}
            isDisabled={isEncounterCancelled}
            onPayerChange={handlePayerChange}
          />

          <PolicyInfoSection
            policyNumber={formData.policyNumber || ''}
            groupNumber={formData.groupNumber || ''}
            groupName={formData.groupName || ''}
            isDisabled={isEncounterCancelled}
            onInputChange={handleInputChange}
          />

          <InsuredInfoSection
            formData={formData}
            sameAsPatient={sameAsPatient}
            isDisabled={isEncounterCancelled}
            onInputChange={handleInputChange}
            onSameAsPatientChange={handleSameAsPatientChange}
          />

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          p: 2.5,
          backgroundColor: '#f8fafc',
          borderTop: '1px solid #e2e8f0'
        }}
      >
        <DialogActionButtons
          isEncounterCancelled={isEncounterCancelled}
          onDelete={handleDelete}
          onClose={handleClose}
          onSubmit={handleSubmit}
        />
      </DialogActions>
    </Dialog>
  );
};

export default InsuranceEditDialog;
